/** Where an instance sits, as the scene cull measures it, and the two ways a drawer places one. */
import * as THREE from 'three';
import { transform3DToMatrix } from '../nodeTreeTransforms';
import { visibleInTree } from '../visibleInTree';
/** Where an instance sits until its drawer places it: nowhere, so the cull cannot measure it. */
export const UNPLACED = Object.freeze({
    nodeMatrixWorld: () => false,
    ownAabb: () => false,
    isVisibleInTree: () => false,
});
/** Writes a Godot AABB into a three box. */
export function copyAabb(target, { position, size }) {
    target.min.set(position.x, position.y, position.z);
    target.max.set(position.x + size.x, position.y + size.y, position.z + size.z);
}
/**
 * An instance whose mesh draws in the node's own space, under the node object's live pose: a
 * MeshInstance3D, or a CSG root, whose mesh is a child of its node group. A mesh with no vertices
 * yet, which is still loading or evaluating, has no box.
 */
export function livePlacement(nodeRef, meshRef) {
    return {
        nodeMatrixWorld: livePose(nodeRef),
        isVisibleInTree: liveVisibility(nodeRef),
        ownAabb(target) {
            const box = meshRef.current && geometryBox(meshRef.current.geometry);
            if (!box)
                return false;
            target.copy(box);
            return true;
        },
    };
}
/**
 * As `livePlacement`, for a mesh of several drawn parts, which share the node's space: one box
 * over all of them, as the rendering server holds one box for the whole mesh.
 */
export function partsPlacement(nodeRef, geometries) {
    return {
        nodeMatrixWorld: livePose(nodeRef),
        isVisibleInTree: liveVisibility(nodeRef),
        ownAabb(target) {
            target.makeEmpty();
            for (const geometry of geometries) {
                const box = geometryBox(geometry);
                if (box)
                    target.union(box);
            }
            return !target.isEmpty();
        },
    };
}
/** The box of `geometry`, or null while it holds no vertices. */
function geometryBox(geometry) {
    if (!geometry.getAttribute('position'))
        return null;
    if (!geometry.boundingBox)
        geometry.computeBoundingBox();
    return geometry.boundingBox;
}
/** An instance whose box its data gives, or none, under the node object's live pose. */
export function boxPlacement(nodeRef, ownAabb) {
    return {
        nodeMatrixWorld: livePose(nodeRef),
        isVisibleInTree: liveVisibility(nodeRef),
        ownAabb(target) {
            if (ownAabb)
                copyAabb(target, ownAabb);
            return ownAabb !== null;
        },
    };
}
function livePose(nodeRef) {
    return (target) => {
        const node = nodeRef.current;
        if (node)
            target.copy(node.matrixWorld);
        return node !== null;
    };
}
function liveVisibility(objectRef) {
    return () => objectRef.current !== null && visibleInTree(objectRef.current);
}
/**
 * An instance whose live pose a billboard or `fixed_size` rewrites every frame, which Godot's
 * instance transform never sees: its authored `transform` under its three parent, and `ownAabb`.
 */
export function authoredPlacement(objectRef, transform, ownAabb) {
    const local = transform ? transform3DToMatrix(transform) : new THREE.Matrix4();
    return {
        nodeMatrixWorld(target) {
            const parent = objectRef.current?.parent;
            if (!parent)
                return false;
            target.multiplyMatrices(parent.matrixWorld, local);
            return true;
        },
        ownAabb(target) {
            copyAabb(target, ownAabb);
            return true;
        },
        isVisibleInTree: liveVisibility(objectRef),
    };
}
//# sourceMappingURL=placements.js.map